import React, { useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { AuthHeader, MobileVerificationForm, ModalFactory, ModalLayout } from 'components';
import AccountRecoveryAddIdentity from 'components/AccountRecovery/AccountRecoveryAddIdentity';
import validations from 'lib/Validations';
import { routeConstants } from 'routes';
import { selectAccountRecovery } from 'store/AccountRecovery';
import * as accountRecoveryActions from 'store/AccountRecovery/actions';
import * as ActionTypes from 'store/AccountRecovery/types';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { ChannelType } from 'types';
import { stripCountryCode } from 'utils';

const AccountRecoveryIdentity: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.AccountRecovery.ADD_IDENTITY_REQUEST));
  const accountRecovery = useSelector(selectAccountRecovery());
  const accountRecoveryErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, ActionTypes.AccountRecovery.ADD_IDENTITY_REQUEST));
  const [hasResentPasscode, setHasResentPasscode] = useState<boolean>(false);
  const verifyLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.AccountRecovery.VERIFY_IDENTITY_REQUEST));
  const apiErrorsVerify = useSelector((state: IStore) => selectAPIRequestErrors(state, ActionTypes.AccountRecovery.VERIFY_IDENTITY_REQUEST));
  const resendLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.AccountRecovery.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST));

  // Redirect to login if we don't have an account recovery or the token needed to add an identity.
  if (!accountRecovery || !accountRecovery.token) {
    history.replace(routeConstants.public.login);
  }

  const validateField = (field: 'identity') => Boolean(touched[field] && errors[field]);
  const { values, errors, touched, isValid, dirty, handleSubmit, setFieldValue, handleBlur, resetForm } = useFormik({
    initialValues: {
      identity: '',
    },
    onSubmit: () => {
      if (accountRecovery && accountRecovery.token) {
        const payload = {
          identity: stripCountryCode(values.identity),
          token: accountRecovery.token,
        };
        dispatch(accountRecoveryActions.addIdentityRequest(accountRecovery.id, payload, onSuccess));
      }
    },
    validateOnBlur: true,
    validationSchema: validations.accountRecoveryAddIdentityValidationSchema,
  });

  // Modal
  const [showModal, hideModal] = useModal(
    () => (
      <ModalFactory
        customModal
        isActive
        onClose={() => {
          hideModal();
        }}
        closeOnOutSideClick={false}
      >
        <ModalLayout
          isActive
          onClose={() => {
            dispatch(clearRequest(ActionTypes.AccountRecovery.ADD_IDENTITY_REQUEST));
            dispatch(clearRequest(ActionTypes.AccountRecovery.VERIFY_IDENTITY_REQUEST));
            setHasResentPasscode(false);
            hideModal();
          }}
        >
          <MobileVerificationForm
            mobile={values.identity}
            isLoading={verifyLoading}
            isResendLoading={resendLoading}
            errorMessages={apiErrorsVerify?.errorMessages}
            onSubmit={onVerifySubmit}
            resendVerificationCode={resendSms}
            resendCall={resendCall}
            showCallButton={hasResentPasscode}
          />
        </ModalLayout>
      </ModalFactory>
    ),
    [dispatch, accountRecovery, hasResentPasscode, resendLoading, verifyLoading, values.identity]
  );

  const onSuccess = () => {
    showModal();
  };

  const onVerifySuccess = () => {
    history.push(routeConstants.private.rootPath);
  };

  const onVerifySubmit = (verificationCode: string) => {
    if (verificationCode && accountRecovery) {
      if (accountRecovery.mobileIdentity) {
        const payload = {
          verifyCode: verificationCode,
        };

        dispatch(accountRecoveryActions.verifyIdentityRequest(accountRecovery.id, accountRecovery.mobileIdentity.id, payload, onVerifySuccess));
      }
    }
  };

  const resendSms = () => {
    if (accountRecovery && accountRecovery.mobileIdentity) {
      dispatch(accountRecoveryActions.resendIdentityVerificationCodeRequest(accountRecovery.id, accountRecovery.mobileIdentity.id));
      setHasResentPasscode(true);
    }
  };

  const resendCall = () => {
    if (accountRecovery && accountRecovery.mobileIdentity) {
      dispatch(accountRecoveryActions.resendIdentityVerificationCodeRequest(accountRecovery.id, accountRecovery.mobileIdentity.id, ChannelType.CALL, 'Verification call initiated'));
      setHasResentPasscode(true);
    }
  };

  const handleOnBack = () => {
    resetForm();
    history.goBack();
  };

  return (
    <AccountRecoveryAddIdentity
      authHeader={<AuthHeader data-cy="account-recovery-header" title="Account Recovery" subtitle="Enter your mobile number." onBack={() => handleOnBack()} />}
      onSubmit={handleSubmit}
      validateField={validateField}
      setFieldValue={setFieldValue}
      isValid={isValid}
      dirty={dirty}
      errorMessages={accountRecoveryErrors?.errorMessages}
      errors={errors}
      isLoading={isLoading}
      handleBlur={handleBlur}
    />
  );
};

export default AccountRecoveryIdentity;
